import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, MessageCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { WHATSAPP_BUSINESS_NUMBER, PREMIUM_PRICE_LABEL } from "@/constants/subscription";
import { toast } from "@/hooks/use-toast";

interface SubscriptionUpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  limitType: 'goals' | 'pledges' | 'connections' | 'certificates';
  currentCount?: number;
  limit?: number;
}

export function SubscriptionUpgradeDialog({
  open,
  onOpenChange,
  limitType,
  currentCount,
  limit
}: SubscriptionUpgradeDialogProps) {
  
  const getLimitMessage = () => {
    switch (limitType) {
      case 'goals':
        return {
          emoji: '🎯',
          title: "You've reached your free limit of 4 active goals!",
          description: "Upgrade to Premium to create unlimited goals and supercharge your goal achievement journey!"
        };
      case 'pledges':
        return {
          emoji: '🎁',
          title: "You've sent 5 gift pledges this month (free limit)!",
          description: "Upgrade to Premium to send unlimited gifts and motivate your loved ones without limits!"
        };
      case 'connections':
        return {
          emoji: '👥',
          title: "Connect with unlimited family members!",
          description: "Upgrade to Premium to add more connections and build your support network!"
        };
      case 'certificates':
        return {
          emoji: '🏆',
          title: "You've created 3 certificates this month (free limit)!",
          description: "Upgrade to Premium to create unlimited certificates with permanent storage and deletion control!"
        };
    }
  };

  const message = getLimitMessage();

  const handleContactUs = () => {
    if (!WHATSAPP_BUSINESS_NUMBER) {
      toast({
        title: "Premium upgrades coming soon",
        description: "We're finalising our billing channel. Please check back shortly!",
      });
      return;
    }
    const whatsappMessage = encodeURIComponent(
      `Hi! I'd like to upgrade to Goal Digger Premium (${PREMIUM_PRICE_LABEL}) 🚀`
    );
    window.open(`https://wa.me/${WHATSAPP_BUSINESS_NUMBER}?text=${whatsappMessage}`, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-primary" />
            Unlock Your Full Potential!
          </DialogTitle>
          <DialogDescription className="text-base pt-2">
            {message.emoji} {message.title}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Current Status */}
          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-sm text-muted-foreground">
              You're currently on the <span className="font-semibold text-foreground">FREE plan</span>
            </p>
            {currentCount !== undefined && limit !== undefined && (
              <p className="text-xs text-muted-foreground mt-1">
                Using {currentCount} of {limit} {limitType}
              </p>
            )}
          </div>

          {/* Comparison Cards */}
          <div className="grid md:grid-cols-2 gap-4">
            {/* Free Tier */}
            <Card className="p-4 border-2">
              <h3 className="font-semibold mb-3 text-muted-foreground">FREE</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <span>4 active goals</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <span>5 gift pledges/month</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <span>1 parent connection</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <span>3 certificates/month (30-day storage)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <span>Send WhatsApp gifts manually</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <span>In-app notifications</span>
                </li>
              </ul>
            </Card>

            {/* Premium Tier */}
            <Card className="p-4 border-2 border-primary bg-primary/5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-primary">PREMIUM</h3>
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary">AED 5</div>
                  <div className="text-xs text-muted-foreground">/month</div>
                </div>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 text-primary" />
                  <span className="font-medium">Unlimited goals</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 text-primary" />
                  <span className="font-medium">Unlimited gift pledges</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 text-primary" />
                  <span className="font-medium">Unlimited connections</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 text-primary" />
                  <span className="font-medium">Unlimited certificates (permanent)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 text-primary" />
                  <span className="font-medium">Delete certificate library</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 text-primary" />
                  <span className="font-medium">WhatsApp notifications</span>
                  <span className="text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded">Coming Soon</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="h-4 w-4 mt-0.5 text-primary" />
                  <span className="font-medium">Priority support</span>
                </li>
              </ul>
            </Card>
          </div>

          {/* Value Proposition */}
          <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-4 text-center">
            <p className="text-sm font-medium">
              ✨ That's less than a coffee per month! ☕
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {message.description}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              onClick={handleContactUs}
              className="flex-1 h-12 text-base font-semibold"
              size="lg"
            >
              <MessageCircle className="mr-2 h-5 w-5" />
              Contact Us on WhatsApp
            </Button>
            <Button 
              onClick={() => onOpenChange(false)}
              variant="outline"
              className="flex-1 h-12"
              size="lg"
            >
              Maybe Later
            </Button>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            We'll respond within 24 hours to activate your Premium subscription
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
